import { log } from './logger';
// @ts-ignore
const convertLazy = () => import('heic-convert/browser');
const JPEG_CONVERSION_QUALITY = 0.85;

/**
 * Checks if a file is a HEIC/HEIF image.
 *
 * @param file The file to check
 * @returns true if the file is a HEIC/HEIF image
 */
export function isHeicFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return name.endsWith('.heic') || name.endsWith('.heif');
}

/**
 * Checks if a file is a PDF.
 *
 * @param file The file to check
 * @returns true if the file is a PDF
 */
export function isPdfFile(file: File): boolean {
    if (file.type === 'application/pdf') return true;
    const name = file.name.toLowerCase();
    return name.endsWith('.pdf');
}

/**
 * Checks if a file is a video.
 *
 * @param file The file to check
 * @returns true if the file is a video
 */
export function isVideoFile(file: File): boolean {
    if (file.type && file.type.startsWith('video/')) return true;
    const name = file.name.toLowerCase();
    return (
        name.endsWith('.mp4') ||
        name.endsWith('.mov') ||
        name.endsWith('.avi') ||
        name.endsWith('.m4v') ||
        name.endsWith('.mpeg') ||
        name.endsWith('.mpg') ||
        name.endsWith('.webm') ||
        name.endsWith('.mkv')
    );
}

/**
 * Checks if a file is an audio file.
 *
 * @param file The file to check
 * @returns true if the file is an audio file
 */
export function isAudioFile(file: File): boolean {
    if (file.type && file.type.startsWith('audio/')) return true;
    const name = file.name.toLowerCase();
    return (
        name.endsWith('.mp3') ||
        name.endsWith('.wav') ||
        name.endsWith('.aac') ||
        name.endsWith('.ogg') ||
        name.endsWith('.m4a') ||
        name.endsWith('.flac')
    );
}

/**
 * Checks if a file is an image based on its type or extension.
 *
 * @param file The file to check
 * @returns true if the file is an image
 */
export function isImageFile(file: File): boolean {
    if (file.type && file.type.startsWith('image/')) return true;
    const name = file.name.toLowerCase();
    return (
        name.endsWith('.jpg') ||
        name.endsWith('.jpeg') ||
        name.endsWith('.png') ||
        name.endsWith('.gif') ||
        name.endsWith('.webp') ||
        name.endsWith('.bmp') ||
        name.endsWith('.svg') ||
        isHeicFile(file)
    );
}

/**
 * Attempts to get a MIME type from a filename if not already present.
 *
 * @param fileName
 * @param existingType
 * @returns
 */
export function getMimeType(fileName: string, existingType?: string): string {
    if (existingType && existingType !== '') return existingType;
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        case 'gif':
            return 'image/gif';
        case 'webp':
            return 'image/webp';
        case 'heic':
            return 'image/heic';
        case 'heif':
            return 'image/heif';
        case 'pdf':
            return 'application/pdf';
        case 'txt':
            return 'text/plain';
        case 'mp4':
            return 'video/mp4';
        case 'mov':
            return 'video/quicktime';
        case 'mpeg':
        case 'mpg':
            return 'video/mpeg';
        case 'm4v':
            return 'video/x-m4v';
        case 'webm':
            return 'video/webm';
        case 'avi':
            return 'video/x-msvideo';
        case 'mp3':
            return 'audio/mpeg';
        case 'wav':
            return 'audio/wav';
        case 'aac':
            return 'audio/aac';
        case 'ogg':
            return 'audio/ogg';
        case 'm4a':
            return 'audio/mp4';
        case 'flac':
            return 'audio/flac';
        default:
            return existingType || 'application/octet-stream';
    }
}

/**
 * Converts a HEIC file to a JPEG blob.
 * Loads the heic-convert/browser library lazily on the first call.
 *
 * @param file The HEIC file to convert
 * @returns A Promise that resolves to a JPEG Blob
 */
export async function convertToJpeg(file: File): Promise<Blob> {
    const { default: convert } = await convertLazy();
    const buffer = await file.arrayBuffer();

    const converted = await convert({
        buffer: new Uint8Array(buffer) as any,
        format: 'JPEG',
        quality: JPEG_CONVERSION_QUALITY,
    });

    return new Blob([converted], { type: 'image/jpeg' });
}

/**
 * Generates a thumbnail from the first frame of a video file.
 *
 * @param file The video file
 * @param timeoutMs Timeout in milliseconds (default 10000)
 * @returns A Promise that resolves to a data URL of the thumbnail
 */
export async function getVideoThumbnail(
    file: File,
    timeoutMs = 10000,
): Promise<string> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;

        const url = URL.createObjectURL(file);

        const timeout = setTimeout(() => {
            URL.revokeObjectURL(url);
            reject(new Error('Video thumbnail generation timed out'));
            // Clean up video element
            video.src = '';
            video.load();
        }, timeoutMs);

        video.src = url;

        video.onloadeddata = () => {
            // Seek to a very early point to ensure we get a frame
            video.currentTime = 0.1;
        };

        video.onseeked = () => {
            clearTimeout(timeout);
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
                URL.revokeObjectURL(url);
                resolve(dataUrl);
            } else {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to get canvas context'));
            }
            // Clean up
            video.src = '';
            video.load();
        };

        video.onerror = (e) => {
            clearTimeout(timeout);
            URL.revokeObjectURL(url);
            reject(e);
            video.src = '';
            video.load();
        };
    });
}

/**
 * Internal helper to load an image from a blob/URL and downscale it.
 */
async function downscaleImage(
    sourceUrl: string,
    maxDimension: number,
): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            if (width > height) {
                if (width > maxDimension) {
                    height *= maxDimension / width;
                    width = maxDimension;
                }
            } else {
                if (height > maxDimension) {
                    width *= maxDimension / height;
                    height = maxDimension;
                }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Failed to get canvas context'));
                return;
            }
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to create thumbnail blob'));
                    }
                },
                'image/jpeg',
                0.92,
            );
        };

        img.onerror = (e) => {
            reject(new Error('Image load failed during downscaling'));
        };

        img.src = sourceUrl;
    });
}

/**
 * Generates a downscaled thumbnail from any image file (including HEIC).
 * Optimized for mobile Safari to skip HEIC conversion when supported natively.
 *
 * @param file The image file
 * @param maxDimension The maximum width or height of the thumbnail (default 512)
 * @param timeoutMs Timeout in milliseconds (default 15000)
 * @returns A Promise that resolves to a Blob of the thumbnail (JPEG)
 */
export async function createThumbnail(
    file: File,
    maxDimension = 512,
    timeoutMs = 15000,
): Promise<Blob> {
    const url = URL.createObjectURL(file);

    // Safety timeout to avoid infinite spinners if something goes very wrong
    const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
            () => reject(new Error('Thumbnail generation timed out')),
            timeoutMs,
        ),
    );

    const generate = async (): Promise<Blob> => {
        try {
            // First attempt: direct load (Works for JPEG, PNG, WEBP, and HEIC on Safari)
            return await downscaleImage(url, maxDimension);
        } catch (err) {
            // Second attempt: If it's HEIC and direct load failed, try slow conversion fallback
            if (isHeicFile(file)) {
                log(
                    'Direct HEIC load failed, trying conversion fallback...',
                    file.name,
                );
                const convertedBlob = await convertToJpeg(file);
                const convertedUrl = URL.createObjectURL(convertedBlob);
                try {
                    return await downscaleImage(convertedUrl, maxDimension);
                } finally {
                    URL.revokeObjectURL(convertedUrl);
                }
            }
            throw err;
        } finally {
            URL.revokeObjectURL(url);
        }
    };

    return Promise.race([generate(), timeoutPromise]);
}
